import { defineStore, skipHydrate } from 'pinia';
import type { Location } from '~/types/Location';

// v3: Location gained a required suburbId field. Bumped so a pre-existing v2
// persisted value (missing suburbId) isn't read back as if it were valid —
// it's simply ignored, same as if nothing had ever been persisted.
const STORAGE_KEY = 'votewise:location:v3';

export const useLocationStore = defineStore('location', () => {
  const location = ref<Location | null>(null);

  // Hydrate from localStorage on the client only, deferred to onMounted
  // rather than read synchronously here in setup(). A synchronous read
  // would flip `isSet` to true on the client before hydration has
  // reconciled the server-rendered "unset" markup — Vue's hydration only
  // *warns* about a resulting class mismatch (e.g.
  // VotingAreaCard.vue's `.voting-area-card__pill`/`__chip` v-if/v-else),
  // it never rectifies the DOM, so the adopted node keeps the wrong class
  // forever. Waiting for onMounted lets the client's first render match
  // SSR's "unset" state, then updates reactively afterwards via a normal
  // (non-hydration) patch. Same fix as commit 641355d applied to the
  // location modal's auto-open check — see app/tests/stores/location.spec.ts
  // for the accompanying regression coverage.
  onMounted(() => {
    // One-time cleanup of the superseded v2 entry so it doesn't linger.
    localStorage.removeItem('votewise:location:v2');
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as Partial<Location> | null;
        const isValid =
          !!parsed &&
          typeof parsed.suburbId === 'string' &&
          typeof parsed.suburb === 'string' &&
          Array.isArray(parsed.postcodes) &&
          Array.isArray(parsed.electorates);
        location.value = isValid ? (parsed as Location) : null;
      } catch {
        location.value = null;
      }
    }
  });

  const isSet = computed(() => location.value !== null);

  const displayLabel = computed(() =>
    location.value
      ? [location.value.suburb, 'VIC', location.value.postcodes[0]]
          .filter(Boolean)
          .join(' ')
      : 'Location',
  );

  const persist = () => {
    if (!import.meta.client) return;
    if (location.value) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(location.value));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  };

  const setLocation = (loc: Location) => {
    location.value = loc;
    persist();
  };

  const clearLocation = () => {
    location.value = null;
    persist();
  };

  return {
    // skipHydrate prevents @pinia/nuxt's SSR payload restore (which is
    // always `{ location: null }` server-side, since localStorage is
    // unavailable there) from overwriting the value this setup() just read
    // from localStorage on the client. See app/tests/stores/location.spec.ts
    // for the regression test and full explanation.
    location: skipHydrate(readonly(location) as Readonly<Ref<Location | null>>),
    isSet,
    displayLabel,
    setLocation,
    clearLocation,
  };
});
